import { useCustomTheme } from '../slices/theme/hooks';
import { getFirebaseAuthToken } from '../helpers/firebase_helper';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const useReferralLinkBuilder = () => {
    const theme = useCustomTheme();
    const customBaseUrl = theme && theme.domainConfig && theme.domainConfig.memberPortalDomain
        ? theme.domainConfig.memberPortalDomain
        : process.env.REACT_APP_ENROLLMENT_BASE_URL;

    const ensureProtocol = (url) => {
        if (!url) return url;
        // Check if URL already has a protocol
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        // Add https:// if missing
        return `https://${url}`;
    };

    const getMemberPortalBaseUrl = () => {
        const baseUrl = customBaseUrl || process.env.REACT_APP_ENROLLMENT_BASE_URL || 'http://localhost:3002';
        if (!baseUrl) {
            console.error("Member portal base URL is not defined in environment variables.");
            return "";
        }
        return ensureProtocol(baseUrl);
    }

    const createIndividualReferralLinkFromPromoCode = (promoCode) => {
        return `${ensureProtocol(customBaseUrl)}/enroll?agentCode=${promoCode}`;
    };

    const createIndividualUniqueLink = (individualCode, agentCode) => {
        return `${ensureProtocol(customBaseUrl)}/enroll?individualCode=${individualCode}&agentCode=${agentCode}`;
    }

    const createAgentMemberLinkFromAgentCode = (code) => {
        return `${ensureProtocol(customBaseUrl)}/login?agentCode=${code}`;
    };

    const createEmployeeReferralLink = ({ employerCode, employeeCode }) => {
        return `${ensureProtocol(customBaseUrl)}/enroll?employerCode=${employerCode}&employeeCode=${employeeCode}`;
    };

    /**
     * Fetches the individual enrollment link from the backend API
     * @param {string} individualId - The ID of the individual
     * @returns {Promise<Object>} - Returns an object with enrollmentLink, agentCode, individualCode, etc.
     */
    const fetchIndividualEnrollmentLink = async (individualId) => {
        try {
            const token = await getFirebaseAuthToken();
            const response = await fetch(
                `${API_BASE}/dashboard/individuals/${individualId}/enrollment-link`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${token}`,
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error fetching individual enrollment link');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching individual enrollment link:', error);
            throw error;
        }
    };

    return {
        createIndividualReferralLinkFromPromoCode,
        createAgentMemberLinkFromAgentCode,
        createEmployeeReferralLink,
        getMemberPortalBaseUrl,
        createIndividualUniqueLink,
        fetchIndividualEnrollmentLink
    };
};